import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useVisible } from '~/hooks/useVisible';
import { People } from '~/services/Http';
import { EnumTranslationDto } from '~/services/Http/client.people.generated';

import { selectAbsenceColor } from '../../utils';

import { useSxStyles } from './useStyles';

export interface Props {
  teamAbsences: People.AbsenceDto[];
}

export const Legend: React.FC<Props> = ({ teamAbsences }) => {
  const {
    isVisible: open,
    show: handleOpenLegend,
    hide: handleClose,
  } = useVisible();

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const absenceReasonsObj = teamAbsences.reduce(
    (acc, abs) => ({ ...acc, [abs.reason.code]: abs.reason }),
    {} as { [key: string]: EnumTranslationDto },
  );

  const legendItems = React.useCallback(() => {
    return Object.values(absenceReasonsObj).map((abs) => {
      const leaveTypeColorBox = selectAbsenceColor(abs.code, abs.value);

      return (
        <Box key={abs.code} sx={sxStyles.divWrapper}>
          <Box
            component="span"
            sx={{ ...sxStyles.main, backgroundColor: leaveTypeColorBox }}
          ></Box>
          <Box component="span" sx={sxStyles.absenceLabel}>
            {abs.value}
          </Box>
        </Box>
      );
    });
  }, [
    sxStyles.absenceLabel,
    sxStyles.divWrapper,
    sxStyles.main,
    absenceReasonsObj,
  ]);

  return (
    <>
      <Button color="primary" onClick={handleOpenLegend}>
        {t('legend')}
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('legend')}</DialogTitle>
        <DialogContent>
          <Box sx={sxStyles.contentWrapper}>
            <Box sx={sxStyles.columnWrapper}>{legendItems()}</Box>

            <Box sx={sxStyles.columnWrapper}>
              <Box sx={sxStyles.divWrapper}>
                <Box
                  component="span"
                  sx={{ ...sxStyles.main, ...sxStyles.empty }}
                >
                  ?
                </Box>
                <Box component="span" sx={sxStyles.absenceLabel}>
                  {t('notApproved')}
                </Box>
              </Box>
              <Box sx={sxStyles.divWrapper}>
                <Box
                  component="span"
                  sx={{ ...sxStyles.main, ...sxStyles.empty }}
                >
                  L
                </Box>
                <Box component="span" sx={sxStyles.absenceLabel}>
                  {t('longTermAbsence')}
                </Box>
              </Box>
              <Box sx={sxStyles.divWrapper}>
                <Box
                  component="span"
                  sx={{ ...sxStyles.main, ...sxStyles.empty }}
                >
                  ★
                </Box>
                <Box component="span" sx={sxStyles.absenceLabel}>
                  {t('noEndDate')}
                </Box>
              </Box>
              <Box sx={sxStyles.divWrapper}>
                <Box
                  component="span"
                  sx={{ ...sxStyles.main, ...sxStyles.publicHoliday }}
                >
                  PH
                </Box>
                <Box component="span" sx={sxStyles.absenceLabel}>
                  {t('publicHoliday')}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" autoFocus>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
